import { useEffect, useMemo, useState } from "react";
import { Megaphone, Plus, Send, Download, Trash2, Sparkles, X, Clock, Users, AlertTriangle, CheckCircle2, Pencil, Mail, BarChart3, Copy as CopyIcon, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const SEGMENT_LABELS = {
  all: "Everyone who applied",
  closed_won: "Customers (Closed Won)",
  closed_lost: "Lost deals (Closed Lost)",
  unresponsive: "Unresponsive leads",
};

const STATUS_STYLE = {
  draft: "text-slate-300 bg-white/10 border-white/15",
  scheduled: "text-amber-300 bg-amber-500/15 border-amber-500/30",
  sending: "text-sky-300 bg-sky-500/15 border-sky-500/30",
  sent: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30",
  partial: "text-amber-300 bg-amber-500/15 border-amber-500/30",
  failed: "text-rose-300 bg-rose-500/15 border-rose-500/30",
};

function mdPreview(text) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) =>
    esc(s)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" style="color:#4690d9">$1</a>');
  const blocks = (text || "").trim().split(/\n\s*\n/);
  return blocks
    .filter((b) => b.trim())
    .map((b) => {
      const lines = b.split("\n");
      if (lines.every((l) => !l.trim() || /^[-*]\s/.test(l.trim()))) {
        return (
          '<ul style="margin:0 0 14px;padding-left:20px">' +
          lines.filter((l) => l.trim()).map((l) => "<li>" + inline(l.trim().slice(2)) + "</li>").join("") +
          "</ul>"
        );
      }
      const f = lines[0].trim();
      if (f.startsWith("## ")) return '<h2 style="margin:0 0 10px;color:#0a1830;font-size:18px">' + inline(f.slice(3)) + "</h2>";
      if (f.startsWith("# ")) return '<h1 style="margin:0 0 12px;color:#0a1830;font-size:22px">' + inline(f.slice(2)) + "</h1>";
      return '<p style="margin:0 0 14px;line-height:1.6;color:#2b3a4a">' + lines.map(inline).join("<br>") + "</p>";
    })
    .join("");
}

const inputCls =
  "w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9]";

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [config, setConfig] = useState({ sending_configured: false, from_email: "", from_name: "", webhook_url: "" });
  const [statsMap, setStatsMap] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null); // campaign object being composed

  const load = async () => {
    const [c, a, cfg] = await Promise.all([
      api.get("/campaigns"),
      api.get("/campaigns/audiences"),
      api.get("/campaigns/config"),
    ]);
    setCampaigns(c.data);
    setAudiences(a.data.segments);
    setConfig(cfg.data);
    setLoaded(true);
    const tracked = c.data.filter((x) => ["sent", "partial", "sending"].includes(x.status));
    const entries = await Promise.all(
      tracked.map((x) => api.get(`/campaigns/${x.id}/stats`).then((r) => [x.id, r.data]).catch(() => null))
    );
    setStatsMap(Object.fromEntries(entries.filter(Boolean)));
  };
  useEffect(() => {
    load().catch(() => toast.error("Couldn't load campaigns"));
  }, []);

  const newCampaign = async () => {
    try {
      const { data } = await api.post("/campaigns", { name: "Untitled campaign", segment: "all" });
      setCampaigns((p) => [data, ...p]);
      setEditing(data);
    } catch {
      toast.error("Couldn't create campaign");
    }
  };

  const removeCampaign = async (id) => {
    await api.delete(`/campaigns/${id}`);
    setCampaigns((p) => p.filter((c) => c.id !== id));
    toast.success("Campaign deleted");
  };

  const exportAudience = (id) => {
    window.open(`${api.defaults.baseURL}/campaigns/${id}/export`, "_blank");
  };

  const onSaved = (updated) => {
    setCampaigns((p) => p.map((c) => (c.id === updated.id ? updated : c)));
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow mb-1">Marketing</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white flex items-center gap-3">
            <Megaphone className="text-[#9cc6f0]" size={30} /> Campaigns
          </h1>
          <p className="text-sm text-slate-400 mt-1">Newsletters & feature updates to your pipeline buckets — GrowthOS branded</p>
        </div>
        <button
          data-testid="new-campaign-btn"
          onClick={newCampaign}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-sm font-semibold transition-colors"
        >
          <Plus size={16} /> New campaign
        </button>
      </div>

      {loaded && !config.sending_configured && (
        <div className="glass-panel p-4 border border-amber-500/30 flex items-start gap-3 fade-up" data-testid="sending-not-configured">
          <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-slate-300">
            <p className="font-semibold text-amber-200">Live sending isn't connected yet</p>
            <p className="mt-0.5">
              Add your <span className="font-semibold">SendGrid API key</span> as <code className="text-[#9cc6f0]">SENDGRID_API_KEY</code> (Settings → Secrets)
              and verify <span className="font-semibold">gary@growthosagent.com</span> as a sender in SendGrid. Until then you can still draft, preview and export audiences.
            </p>
          </div>
        </div>
      )}

      {loaded && config.sending_configured && config.webhook_url && (
        <WebhookSetup url={config.webhook_url} />
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 fade-up">
        {audiences.map((a) => (
          <div key={a.key} className="glass-panel p-4" data-testid={`audience-${a.key}`}>
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <Users size={13} /> {a.label}
            </div>
            <p className="text-2xl font-extrabold text-white font-mono-tech">{a.count}</p>
          </div>
        ))}
      </div>

      <div className="glass-panel p-5 fade-up" data-testid="campaigns-list">
        <h2 className="text-lg font-bold text-white mb-4">All campaigns</h2>
        {!loaded ? (
          <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
        ) : campaigns.length === 0 ? (
          <p className="text-sm text-slate-600 py-8 text-center">No campaigns yet. Hit “New campaign” to write your first newsletter.</p>
        ) : (
          <div className="space-y-2">
            {campaigns.map((c) => (
              <div
                key={c.id}
                className="rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] transition-colors p-4 flex flex-wrap items-center gap-3"
                data-testid={`campaign-row-${c.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-white truncate">{c.name}</p>
                    <span className={`text-[10px] font-mono-tech uppercase rounded-full px-2 py-0.5 border ${STATUS_STYLE[c.status] || STATUS_STYLE.draft}`}>
                      {c.status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    {SEGMENT_LABELS[c.segment] || c.segment}
                    {c.subject ? ` · ${c.subject}` : ""}
                    {c.status === "sent" || c.status === "partial" ? ` · ${c.stats?.sent || 0}/${c.stats?.recipients || 0} sent` : ""}
                    {c.status === "scheduled" && c.scheduled_at ? ` · ${new Date(c.scheduled_at).toLocaleString()}` : ""}
                  </p>
                  {statsMap[c.id] && (c.status === "sent" || c.status === "partial") && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2" data-testid={`campaign-stats-${c.id}`}>
                      <Stat label="Delivered" value={`${statsMap[c.id].delivery_rate}%`} sub={`${statsMap[c.id].delivered}/${statsMap[c.id].sent}`} color="text-emerald-300" />
                      <Stat label="Opened" value={`${statsMap[c.id].open_rate}%`} sub={`${statsMap[c.id].opened}`} color="text-sky-300" />
                      <Stat label="Clicked" value={`${statsMap[c.id].click_rate}%`} sub={`${statsMap[c.id].clicked}`} color="text-[#9cc6f0]" />
                      {statsMap[c.id].bounced > 0 && <Stat label="Bounced" value={`${statsMap[c.id].bounced}`} color="text-rose-300" />}
                      {statsMap[c.id].unsubscribed > 0 && <Stat label="Unsub" value={`${statsMap[c.id].unsubscribed}`} color="text-slate-400" />}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    data-testid={`campaign-export-${c.id}`}
                    onClick={() => exportAudience(c.id)}
                    title="Export audience CSV"
                    className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    <Download size={15} />
                  </button>
                  <button
                    data-testid={`campaign-edit-${c.id}`}
                    onClick={() => setEditing(c)}
                    title={["sent", "sending", "partial"].includes(c.status) ? "View" : "Edit"}
                    className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    data-testid={`campaign-delete-${c.id}`}
                    onClick={() => removeCampaign(c.id)}
                    title="Delete"
                    className="p-2 rounded-full text-slate-400 hover:text-rose-400 hover:bg-white/5"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <Composer
          campaign={editing}
          audiences={audiences}
          config={config}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
          onReload={load}
        />
      )}
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className={`text-sm font-extrabold font-mono-tech ${color}`}>{value}</span>
      <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
      {sub && <span className="text-[10px] text-slate-600">({sub})</span>}
    </div>
  );
}

function WebhookSetup({ url }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="glass-panel p-4 fade-up" data-testid="webhook-setup">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-200">
          <BarChart3 size={16} className="text-[#9cc6f0]" /> Delivery tracking setup
          <span className="text-[10px] font-normal text-slate-500">(one-time — enables open & click rates)</span>
        </span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 text-sm text-slate-400 space-y-2">
          <p>
            In SendGrid go to <span className="text-slate-300 font-semibold">Settings → Mail Settings → Event Webhook</span>, turn it on, and paste this as the <span className="text-slate-300 font-semibold">HTTP Post URL</span>. Enable the events: Delivered, Opened, Clicked, Bounced, Spam Reports, Unsubscribes.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[11px] text-[#9cc6f0] break-all" data-testid="webhook-url">{url}</code>
            <button
              data-testid="copy-webhook-url"
              onClick={() => { navigator.clipboard.writeText(url); toast.success("Webhook URL copied"); }}
              className="shrink-0 p-2 rounded-lg border border-white/15 text-slate-300 hover:text-white"
            >
              <CopyIcon size={14} />
            </button>
          </div>
          <p className="text-[11px] text-slate-500">Stats update automatically once the webhook is on and a campaign has been sent.</p>
        </div>
      )}
    </div>
  );
}

function Composer({ campaign, audiences, config, onClose, onSaved, onReload }) {
  const locked = ["sent", "sending", "partial"].includes(campaign.status);
  const [name, setName] = useState(campaign.name || "");
  const [segment, setSegment] = useState(campaign.segment || "all");
  const [subject, setSubject] = useState(campaign.subject || "");
  const [body, setBody] = useState(campaign.body || "");
  const [scheduledAt, setScheduledAt] = useState(
    campaign.scheduled_at ? new Date(campaign.scheduled_at).toISOString().slice(0, 16) : ""
  );
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const count = useMemo(() => audiences.find((a) => a.key === segment)?.count ?? 0, [audiences, segment]);
  const isFuture = scheduledAt && new Date(scheduledAt).getTime() > Date.now();

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const payload = {
        name,
        segment,
        subject,
        body,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      };
      const { data } = await api.patch(`/campaigns/${campaign.id}`, payload);
      onSaved(data);
      if (!silent) toast.success("Saved");
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const aiDraft = async () => {
    if (!aiTopic.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/campaigns/ai-draft", { topic: aiTopic, segment });
      setSubject(data.subject || subject);
      setBody(data.body || body);
      setAiOpen(false);
      setAiTopic("");
      toast.success("Draft ready — tweak it, then send");
    } catch {
      toast.error("AI draft failed, try again");
    } finally {
      setAiLoading(false);
    }
  };

  const sendTest = async () => {
    if (!config.sending_configured) {
      toast.message("Connect your Resend key to send test emails");
      return;
    }
    const saved = await save(true);
    if (!saved) return;
    try {
      await api.post(`/campaigns/${campaign.id}/test`, { email: testEmail });
      toast.success(`Test sent to ${testEmail}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    }
  };

  const sendNow = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Add a subject and body first");
      return;
    }
    if (!config.sending_configured) {
      toast.message("Connect your Resend API key to send campaigns");
      return;
    }
    const verb = isFuture ? "schedule" : "send";
    if (!window.confirm(`${isFuture ? "Schedule" : "Send"} this campaign to ${count} ${SEGMENT_LABELS[segment]}?`)) return;
    const saved = await save(true);
    if (!saved) return;
    setSending(true);
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/send`);
      toast.success(data.status === "scheduled" ? "Campaign scheduled" : `Sending to ${count} recipients…`);
      await onReload();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't ${verb}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="campaign-composer">
      <div className="glass-panel w-full max-w-5xl my-8 p-6 relative">
        <button
          data-testid="composer-close"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/5"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-2 mb-5">
          <h2 className="text-xl font-bold text-white">{locked ? "Campaign" : "Compose campaign"}</h2>
          <span className={`text-[10px] font-mono-tech uppercase rounded-full px-2 py-0.5 border ${STATUS_STYLE[campaign.status] || STATUS_STYLE.draft}`}>
            {campaign.status}
          </span>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* left: edit */}
          <div className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Campaign name (internal)</label>
              <input data-testid="composer-name" className={inputCls} value={name} disabled={locked} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Audience</label>
              <select
                data-testid="composer-segment"
                className={inputCls}
                value={segment}
                disabled={locked}
                onChange={(e) => setSegment(e.target.value)}
              >
                {Object.entries(SEGMENT_LABELS).map(([k, v]) => {
                  const c = audiences.find((a) => a.key === k)?.count ?? 0;
                  return (
                    <option key={k} value={k} className="bg-[#0a1830]">
                      {v} ({c})
                    </option>
                  );
                })}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                {count} recipient{count === 1 ? "" : "s"} · from {config.from_name} &lt;{config.from_email}&gt;
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-slate-400">Subject line</label>
                {!locked && (
                  <button
                    data-testid="composer-ai-btn"
                    onClick={() => setAiOpen((o) => !o)}
                    className="text-[11px] font-mono-tech uppercase text-[#9cc6f0] hover:text-white flex items-center gap-1"
                  >
                    <Sparkles size={12} /> AI draft
                  </button>
                )}
              </div>
              <input data-testid="composer-subject" className={inputCls} value={subject} disabled={locked} onChange={(e) => setSubject(e.target.value)} placeholder="Big news from GrowthOS" />
            </div>
            {aiOpen && !locked && (
              <div className="rounded-lg border border-[#4690d9]/30 bg-[#4690d9]/[0.06] p-3 space-y-2" data-testid="ai-draft-box">
                <label className="text-xs text-slate-300">What's this email about?</label>
                <textarea
                  data-testid="ai-topic-input"
                  className={inputCls}
                  rows={2}
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  placeholder="e.g. We just shipped a new ad-analysis agent — tell customers what it does and invite them to try it"
                />
                <button
                  data-testid="ai-generate-btn"
                  onClick={aiDraft}
                  disabled={aiLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-xs font-semibold disabled:opacity-60"
                >
                  <Sparkles size={13} /> {aiLoading ? "Writing…" : "Generate"}
                </button>
              </div>
            )}
            <div>
              <label className="text-xs text-slate-400 mb-1 block">
                Body <span className="text-slate-600">(## heading · **bold** · - bullets · [text](url))</span>
              </label>
              <textarea
                data-testid="composer-body"
                className={`${inputCls} font-mono text-xs leading-relaxed`}
                rows={12}
                value={body}
                disabled={locked}
                onChange={(e) => setBody(e.target.value)}
                placeholder={"## Hey there\n\nWe just shipped something you'll love...\n\n- Point one\n- Point two\n\n[See what's new](https://growthosagent.com)"}
              />
            </div>
          </div>

          {/* right: preview */}
          <div className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Preview</label>
              <div className="rounded-lg overflow-hidden border border-white/10 bg-[#eef2f7]" data-testid="composer-preview">
                <div style={{ background: "#0a1830", padding: "20px 24px" }}>
                  <span style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: 22, fontWeight: 800, letterSpacing: "-0.5px", lineHeight: 1 }}>
                    <span style={{ color: "#9cc6f0" }}>Growth</span>
                    <span style={{ color: "#fff" }}>OS</span>
                  </span>
                </div>
                <div style={{ padding: 24, maxHeight: 360, overflowY: "auto" }} dangerouslySetInnerHTML={{ __html: mdPreview(body) || '<p style="color:#8a97a6">Your email preview shows up here…</p>' }} />
                <div style={{ padding: "14px 24px", background: "#f5f7fa", borderTop: "1px solid #e3e8ef", fontSize: 12, color: "#8a97a6" }}>
                  Sent by GrowthOS · <span style={{ textDecoration: "underline" }}>Unsubscribe</span>
                </div>
              </div>
            </div>

            {!locked && (
              <>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
                    <Clock size={12} /> Schedule (optional — leave blank to send now)
                  </label>
                  <input
                    data-testid="composer-schedule"
                    type="datetime-local"
                    className={inputCls}
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
                    <Mail size={12} /> Send a test to yourself
                  </label>
                  <div className="flex gap-2">
                    <input
                      data-testid="composer-test-email"
                      className={inputCls}
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="you@thexscale.com"
                    />
                    <button
                      data-testid="composer-test-btn"
                      onClick={sendTest}
                      className="shrink-0 px-3 py-2 rounded-lg border border-white/15 text-slate-300 hover:text-white text-xs font-semibold"
                    >
                      Send test
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {!locked && (
          <div className="flex flex-wrap items-center justify-end gap-2 mt-6 pt-4 border-t border-white/10">
            <button
              data-testid="composer-save-btn"
              onClick={() => save(false)}
              disabled={saving}
              className="px-4 py-2 rounded-full border border-white/15 text-slate-300 hover:text-white text-sm font-semibold disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save draft"}
            </button>
            <button
              data-testid="composer-send-btn"
              onClick={sendNow}
              disabled={sending}
              className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {isFuture ? <Clock size={15} /> : <Send size={15} />}
              {sending ? "Working…" : isFuture ? `Schedule for ${count}` : `Send now to ${count}`}
            </button>
          </div>
        )}
        {locked && campaign.status === "sent" && (
          <div className="mt-6 pt-4 border-t border-white/10 flex items-center gap-2 text-emerald-300 text-sm">
            <CheckCircle2 size={16} /> Sent to {campaign.stats?.sent || 0} of {campaign.stats?.recipients || 0} recipients
          </div>
        )}
        {locked && campaign.error && (
          <div className="mt-6 pt-4 border-t border-white/10 flex items-center gap-2 text-rose-300 text-sm">
            <AlertTriangle size={16} /> {campaign.error}
          </div>
        )}
      </div>
    </div>
  );
}
